import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { membersApi } from '../services/api';
import { Member, MemberInput } from '../types/api';
import { Plus, Edit, Trash2, Search, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { ResponsiveModal, ResponsiveButton } from '../components/ResponsiveModal';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

interface MemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member?: Member;
}

const MemberModal: React.FC<MemberModalProps> = ({ isOpen, onClose, member }) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<MemberInput>({
    defaultValues: { name: '', legal_name: '', country: '', website_url: '', external_id: '', is_active: true }
  });

  // Reset form when member changes
  useEffect(() => {
    if (member) {
      reset({
        name: member.name,
        legal_name: member.legal_name || '',
        country: member.country || '',
        website_url: member.website_url || '',
        external_id: member.external_id || '',
        is_active: member.is_active
      });
    } else {
      reset({ name: '', legal_name: '', country: '', website_url: '', external_id: '', is_active: true });
    }
  }, [member, reset]);

  const createMutation = useMutation({
    mutationFn: membersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      toast.success('Member created successfully');
      onClose();
      reset();
    },
    onError: () => {
      toast.error('Failed to create member');
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: number; member: MemberInput }) => membersApi.update(data.id, data.member),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      toast.success('Member updated successfully');
      onClose();
    },
    onError: () => {
      toast.error('Failed to update member')
    }
  });

  const onSubmit = (data: MemberInput) => {
    if (member) {
      updateMutation.mutate({ id: member.member_id, member: data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={member ? 'Edit Member' : 'Add New Member'}
      size="md"
      footer={
        <>
          <ResponsiveButton
            variant="secondary"
            onClick={onClose}
            fullWidth
            className="sm:w-auto"
          >
            Cancel
          </ResponsiveButton>
          <ResponsiveButton
            variant="primary"
            type="submit"
            form="member-form"
            disabled={createMutation.isPending || updateMutation.isPending}
            fullWidth
            className="sm:w-auto sm:ml-3"
          >
            {createMutation.isPending || updateMutation.isPending 
              ? 'Saving...' 
              : member ? 'Update' : 'Create'
            }
          </ResponsiveButton>
        </>
      }
    >
      <form id="member-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            {...register('name', { required: 'Name is required' })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Organization name"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Legal Name
          </label>
          <input
            {...register('legal_name')}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Legal organization name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <input
            {...register('country')}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Country"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Website URL
          </label>
          <input
            {...register('website_url')}
            type="url"
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            External ID
          </label>
          <input
            {...register('external_id', { 
              maxLength: { value: 18, message: 'External ID must be 18 characters or less' } 
            })}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Salesforce record ID (18 chars)"
            maxLength={18}
          />
          {errors.external_id && (
            <p className="text-red-500 text-sm mt-1">{errors.external_id.message}</p>
          )}
          <p className="text-gray-500 text-xs mt-1">Optional 18-character Salesforce record ID</p>
        </div>

        <div className="flex items-center">
          <input
            {...register('is_active')}
            type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-900">
            Active member
          </label>
        </div>
      </form>
    </ResponsiveModal>
  );
};

export const Members: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingMember, setDeletingMember] = useState<Member | undefined>();
  const queryClient = useQueryClient();

  const { data: members, isLoading } = useQuery({ queryKey: ['members'], queryFn: membersApi.getAll });

  const handleEdit = (member: Member) => {
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const handleDelete = (member: Member) => {
    setDeletingMember(member);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['members'] });
  };

  const handleAdd = () => {
    setEditingMember(undefined);
    setIsModalOpen(true);
  };

  const filteredMembers = members?.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.country?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-san-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-san-gray-900 sm:text-3xl sm:truncate">
            Members
          </h1>
          <p className="mt-1 text-sm text-san-gray-500">
            Manage SAN member organizations
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button onClick={handleAdd} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Add Member
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-san-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-san-gray-300 rounded-md leading-5 bg-white placeholder-san-gray-500 focus:outline-none focus:placeholder-san-gray-400 focus:ring-1 focus:ring-san-green focus:border-san-green sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="stat-card">
          <div className="flex items-center">
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="stat-label">Total Members</dt>
                <dd className="stat-value">{members?.length || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="stat-label">Active Members</dt>
                <dd className="stat-value">{members?.filter(m => m.is_active).length || 0}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center">
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="stat-label">Inactive Members</dt>
                <dd className="stat-value">{members?.filter(m => !m.is_active).length || 0}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <ResponsiveTable
        columns={[
          {
            key: 'name',
            label: 'Organization',
            render: (value, row) => (
              <div>
                <div className="font-medium text-gray-900">{value}</div>
                {row.legal_name && (
                  <div className="text-sm text-gray-500">{row.legal_name}</div>
                )}
              </div>
            )
          },
          {
            key: 'country',
            label: 'Country',
            render: (value) => value || '-'
          },
          {
            key: 'website_url',
            label: 'Website',
            render: (value) => value ? (
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <Globe className="w-4 h-4 mr-1" />
                <span className="text-sm">Visit</span>
              </a>
            ) : (
              <span className="text-gray-500">-</span>
            )
          },
          {
            key: 'external_id',
            label: 'External ID',
            render: (value) => value ? (
              <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                {value}
              </span>
            ) : (
              <span className="text-gray-500">-</span>
            )
          },
          {
            key: 'is_active',
            label: 'Status',
            render: (value) => (
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {value ? 'Active' : 'Inactive'}
              </span>
            )
          }
        ]}
        data={filteredMembers}
        keyField="member_id"
        mobileCardFields={['name', 'country', 'is_active']}
        actions={(member) => (
          <>
            <ResponsiveButton
              variant="secondary"
              size="sm"
              onClick={() => handleEdit(member)}
              className="min-w-[44px]"
            >
              <Edit className="w-4 h-4" />
            </ResponsiveButton>
            <ResponsiveButton
              variant="danger"
              size="sm"
              onClick={() => handleDelete(member)}
              className="min-w-[44px]"
            >
              <Trash2 className="w-4 h-4" />
            </ResponsiveButton>
          </>
        )}
      />

      <MemberModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        member={editingMember}
      />

      {deletingMember && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          memberId={deletingMember.member_id}
          memberName={deletingMember.name}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};